import { supabase } from '../lib/supabase';

const PUBLIC_IP_ENDPOINT = 'https://api.ipify.org?format=json';
const PUBLIC_IP_CACHE_TTL = 5 * 60 * 1000;
const PUBLIC_IP_TIMEOUT_MS = 1500;

let cachedIpAddress: { value: string | null; expiresAt: number } | null = null;
let inflightIpLookup: Promise<string | null> | null = null;

const getCachedPublicIp = () => {
  if (!cachedIpAddress) {
    return null;
  }

  if (cachedIpAddress.expiresAt > Date.now()) {
    return cachedIpAddress.value;
  }

  cachedIpAddress = null;
  return null;
};

async function fetchPublicIp(): Promise<string | null> {
  const cached = getCachedPublicIp();
  if (cached !== null) {
    return cached;
  }

  if (inflightIpLookup) {
    return inflightIpLookup;
  }

  if (typeof fetch === 'undefined' || typeof AbortController === 'undefined') {
    return null;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PUBLIC_IP_TIMEOUT_MS);

  inflightIpLookup = fetch(PUBLIC_IP_ENDPOINT, { signal: controller.signal })
    .then(async (response) => {
      if (!response.ok) {
        return null;
      }

      try {
        const data = await response.json();
        const ip = typeof data?.ip === 'string' ? data.ip : null;

        cachedIpAddress = {
          value: ip,
          expiresAt: Date.now() + PUBLIC_IP_CACHE_TTL,
        };

        return ip;
      } catch {
        return null;
      }
    })
    .catch((error) => {
      if ((error as Error)?.name !== 'AbortError') {
        console.warn('Public IP lookup failed:', error);
      }
      return null;
    })
    .finally(() => {
      clearTimeout(timeoutId);
      inflightIpLookup = null;
    });

  return inflightIpLookup;
}

export type SecurityAction =
  | 'login'
  | 'logout'
  | 'failed_login'
  | 'password_reset_request'
  | 'password_change'
  | 'profile_update'
  | 'sensitive_data_access'
  | 'permission_denied'
  | 'suspicious_activity'
  | 'booking_created'
  | 'booking_cancelled'
  | 'payment_processed'
  | 'admin_action'
  | 'impersonation_start'
  | 'impersonation_stop';

export type SecurityStatus = 'success' | 'failure' | 'blocked';

export function profileAuditOrganizationId(
  profile?: { organization_id?: string | null } | null,
): string | undefined {
  const raw = profile?.organization_id;
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

interface LogSecurityEventParams {
  userId?: string;
  organizationId?: string;
  action: SecurityAction;
  resourceType?: string;
  resourceId?: string;
  status: SecurityStatus;
  details?: Record<string, any>;
}

export async function logSecurityEvent({
  userId,
  organizationId,
  action,
  resourceType,
  resourceId,
  status,
  details = {},
}: LogSecurityEventParams): Promise<void> {
  try {
    const ipAddress = await fetchPublicIp();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;

    void (async () => {
      try {
        await supabase.from('security_audit_logs').insert({
          user_id: userId || null,
          organization_id: organizationId || null,
          action,
          resource_type: resourceType || null,
          resource_id: resourceId || null,
          ip_address: ipAddress,
          user_agent: userAgent,
          status,
          details,
        });
      } catch (error) {
        console.error('Failed to log security event:', error);
      }
    })();
  } catch (err) {
    console.error('Security logging error:', err);
  }
}

export type AuthLoginMethod =
  'password' | 'otp' | 'magic_link' | 'passkey' | 'google' | 'enterprise_sso' | 'guest_password';

export type RecordAuthLoginOutcomeParams = {
  identifier: string;
  success: boolean;
  userId?: string;
  reason?: string;
  organizationId?: string;
  method?: AuthLoginMethod;
  event?: string;
};

/** Fire-and-forget login audit with optional auth method metadata. */
export function recordAuthLoginOutcome(params: RecordAuthLoginOutcomeParams): void {
  void logSecurityEvent({
    userId: params.userId,
    organizationId: params.organizationId,
    action: params.success ? 'login' : 'failed_login',
    status: params.success ? 'success' : 'failure',
    details: {
      email: params.identifier,
      reason: params.reason,
      method: params.method,
      ...(params.event ? { event: params.event } : {}),
      timestamp: new Date().toISOString(),
    },
  }).catch((error: unknown) => {
    console.warn('Unable to record auth login outcome:', error);
  });
}

export async function logLoginAttempt(
  email: string,
  success: boolean,
  userId?: string,
  reason?: string,
  organizationId?: string,
): Promise<void> {
  recordAuthLoginOutcome({
    identifier: email,
    success,
    userId,
    reason,
    organizationId,
  });
}

export async function logPasswordChange(
  userId: string,
  success: boolean,
  organizationId?: string,
): Promise<void> {
  await logSecurityEvent({
    userId,
    organizationId,
    action: 'password_change',
    status: success ? 'success' : 'failure',
    details: {
      timestamp: new Date().toISOString(),
    },
  });
}

export async function logSensitiveAccess(
  userId: string,
  resourceType: string,
  resourceId: string,
  organizationId?: string,
): Promise<void> {
  await logSecurityEvent({
    userId,
    organizationId,
    action: 'sensitive_data_access',
    resourceType,
    resourceId,
    status: 'success',
    details: {
      timestamp: new Date().toISOString(),
    },
  });
}

export async function getRecentSecurityEvents(userId?: string, limit = 50, organizationId?: string) {
  const query = supabase
    .from('security_audit_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (userId) {
    query.eq('user_id', userId);
  }

  if (organizationId) {
    query.eq('organization_id', organizationId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Failed to fetch security events:', error);
    return [];
  }

  return data || [];
}

export async function detectSuspiciousActivity(userId: string, organizationId?: string): Promise<boolean> {
  const recentEvents = await getRecentSecurityEvents(userId, 20);

  const failedLoginAttempts = recentEvents.filter(
    (event) =>
      event.action === 'failed_login' && new Date(event.created_at).getTime() > Date.now() - 15 * 60 * 1000,
  );

  if (failedLoginAttempts.length >= 5) {
    await logSecurityEvent({
      userId,
      organizationId,
      action: 'suspicious_activity',
      status: 'blocked',
      details: {
        reason: 'Multiple failed login attempts',
        count: failedLoginAttempts.length,
      },
    });
    return true;
  }

  return false;
}
